import { useEffect, useRef } from 'react';
import { openNewConversation } from '@/features/apps/app-conversation';
import { electronInvoke, isElectronDesktop } from '@/lib/electron-bridge';
import {
  currentShortcut,
  electronAccelerator,
  SHORTCUTS_CHANGED_EVENT,
} from '@/lib/settings-page/keyboard-shortcuts';

/*
 * Dictation from any app (Settings › Keyboard shortcuts › Dictate): the Desktop registers the
 * shortcut system-wide as the app starts; pressing it brings Crowdex forward and starts
 * dictating into the composer in view, or into a new conversation when none is.
 */

type Starter = { start: () => void };
const starters: Starter[] = [];
let installed = false;

/** How long a press made with no composer waits for the new conversation's. */
const NEW_CONVERSATION_MS = 15_000;
let waitingUntil = 0;

/** Gives the Desktop the keys of Dictate; `null` when it has none. */
async function syncGlobalDictationShortcut(): Promise<boolean> {
  if (!isElectronDesktop()) return false;
  const keys = currentShortcut('dictate');
  return electronInvoke<boolean>('dictation_shortcut_set', {
    accelerator: keys === undefined ? null : electronAccelerator(keys),
  }).catch(() => false);
}

function pressed(now = Date.now()): void {
  // The composer mounted last is the one in view.
  const starter = starters.at(-1);
  if (starter !== undefined) {
    starter.start();
    return;
  }
  waitingUntil = now + NEW_CONVERSATION_MS;
  openNewConversation();
}

/** Registers Dictate with the Desktop as the app starts, and keeps it to the user's keys. */
export function installGlobalDictation(): void {
  if (installed || !isElectronDesktop()) return;
  installed = true;
  void syncGlobalDictationShortcut();
  window.addEventListener(SHORTCUTS_CHANGED_EVENT, () => void syncGlobalDictationShortcut());
  window.crowdexDesktop?.listen('dictation-start', () => pressed());
}

/** Lets the global shortcut start this composer's dictation while it can. */
export function useGlobalDictation(start: () => void, enabled: boolean): void {
  const ref = useRef(start);
  ref.current = start;
  useEffect(() => {
    if (!enabled) return;
    installGlobalDictation();
    const starter: Starter = { start: () => ref.current() };
    starters.push(starter);
    // Pressed while no composer was there: this is the conversation opened for it.
    if (waitingUntil >= Date.now()) {
      waitingUntil = 0;
      starter.start();
    }
    return () => {
      starters.splice(starters.indexOf(starter), 1);
    };
  }, [enabled]);
}
